import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { formatDateTime } from '../../../config/appConfig';
import type { SystemHealthSummary } from '../types';
import { heroStatusClass } from '../theme';
import HealthQuickChip from './HealthQuickChip';

interface UserHealthHeroProps {
  data: SystemHealthSummary;
}

const StatusIcon: React.FC<{ status: SystemHealthSummary['overallStatus'] }> = ({ status }) => {
  const { colors } = useTheme();
  const shell = cn('flex h-12 w-12 items-center justify-center rounded-xl shrink-0 shadow-sm', colors.surfaceHover);

  if (status === 'ok') {
    return (
      <div className={cn(shell, 'text-emerald-600 dark:text-emerald-400 ring-2 ring-emerald-500/20')}>
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
    );
  }
  if (status === 'warning') {
    return (
      <div className={cn(shell, 'text-amber-600 dark:text-amber-400 ring-2 ring-amber-500/20')}>
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
    );
  }
  return (
    <div className={cn(shell, 'text-rose-600 dark:text-rose-400 ring-2 ring-rose-500/20')}>
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </div>
  );
};

const UserHealthHero: React.FC<UserHealthHeroProps> = ({ data }) => {
  const { colors } = useTheme();
  const { emailSync, syncStats } = data;

  const title =
    data.overallStatus === 'ok'
      ? 'Email sync is running normally'
      : data.overallStatus === 'warning'
        ? 'Some sync issues need attention'
        : 'Email sync has problems';

  const subtitle =
    data.overallStatus === 'ok'
      ? 'Your mail accounts are syncing on schedule.'
      : 'Review account status below or contact your administrator if issues persist.';

  const lastSyncLabel = syncStats.lastSuccessfulSyncAt
    ? formatDateTime(syncStats.lastSuccessfulSyncAt)
    : 'No successful sync yet';

  const accountsTone =
    emailSync.total === 0
      ? 'neutral'
      : emailSync.error > 0
        ? 'bad'
        : emailSync.warning > 0
          ? 'warn'
          : 'good';

  return (
    <div
      className={cn(
        'rounded-2xl border overflow-hidden shadow-sm',
        colors.border,
        heroStatusClass(data.overallStatus, 'user'),
        colors.surface,
      )}
    >
      <div className="p-5 md:p-6 flex flex-col lg:flex-row lg:items-center gap-5">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <StatusIcon status={data.overallStatus} />
          <div className="min-w-0">
            <h2 className={cn('text-xl md:text-2xl font-bold', colors.text)}>{title}</h2>
            <p className={cn('text-sm mt-1', colors.textMuted)}>{subtitle}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 lg:justify-end">
          <HealthQuickChip
            label="Accounts"
            value={
              emailSync.total === 0
                ? 'None configured'
                : `${emailSync.healthy}/${emailSync.total} healthy`
            }
            tone={accountsTone === 'good' ? 'sky' : accountsTone}
          />
          <HealthQuickChip label="Last sync" value={lastSyncLabel} tone="sky" />
          <HealthQuickChip
            label="Database"
            value={data.database.connected ? 'Online' : 'Offline'}
            tone={data.database.connected ? 'good' : 'bad'}
          />
        </div>
      </div>
    </div>
  );
};

export default UserHealthHero;
